import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';

import { RootState } from '../store';
import { logout, setCredentials } from '../modules/auth';
import { Login, Response } from '../models';
// Imported from the notification module directly rather than through the `.`
// barrel: the barrel re-exports THIS file, so `from '.'` was a circular import,
// and a unit test of the wrapper had to mock the whole services surface to
// intercept one function. Same function, same behaviour.
import { displayNotification } from './notification';

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_BACKEND_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.accessToken;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
      return headers;
    }
  },
});

/**
 * ANONYMOUS AUTH SURFACES — the global errorCode-1030 toast is SUPPRESSED here.
 *
 * core-api builds every custom failure with prepareCustomErrorAppResponse,
 * which stamps errorCode 1030 on all of them, and the activation / reset
 * endpoints still pass DISCRIMINATING text through it ('TOKEN NOT EXIST' vs
 * 'TOKEN ALREADY CONSUMED' vs an expired token, and the sign-in failures). On
 * an anonymous page that text is an account + token-state ORACLE: it tells an
 * unauthenticated caller whether an address has an account and what state its
 * token is in. Those pages (Login, ForgetPassword, NewAccount, NewPassword)
 * each render their OWN fixed, hedged copy for the failure, so the global layer
 * has nothing to add here except the leak — it stays silent instead.
 *
 * It keeps firing, MESSAGE-ONLY, for the authenticated app endpoints, so the
 * contract src/utils/mutation-feedback.ts documents (isReportedByGlobalLayer
 * returns true for 1030) stays true everywhere that helper is consumed — the
 * six feature modules, all of them behind a session. The anonymous auth pages
 * never used mutation-feedback.
 *
 * Every anonymous endpoint lives under `company/authenticate/` except the two
 * emailed-token endpoints on `company/teamMember/` — and that prefix ALSO holds
 * authenticated endpoints (getUserDetails, reassignRole, ...), so those two are
 * matched by exact path, never by prefix.
 */
const ANONYMOUS_AUTH_PATH_PREFIXES = ['company/authenticate/'];

const ANONYMOUS_AUTH_PATHS = [
  'company/teammember/verifytoken',
  'company/teammember/setnewpassword',
];

/** Normalise the request URL the wrapper was handed: `args` is either a bare
 * URL string or a FetchArgs object. Strips the query string, any leading
 * slashes and case, so `/company/authenticate/login` and
 * `company/teamMember/verifyToken?token=x` both classify. */
const requestPath = (args: string | FetchArgs): string | undefined => {
  const url = typeof args === 'string' ? args : args?.url;

  if (typeof url !== 'string' || !url.trim()) {
    return undefined;
  }

  return url.split('?')[0].replace(/^\/+/, '').toLowerCase();
};

/** FAILS CLOSED: a request whose URL cannot be classified is treated as
 * anonymous, i.e. the toast is suppressed. Showing the server's own text on a
 * page that may be unauthenticated is the failure mode with a security cost;
 * withholding a toast the page already covers is the one without. */
const isAnonymousAuthRequest = (args: string | FetchArgs): boolean => {
  const path = requestPath(args);

  if (!path) {
    return true;
  }

  return (
    ANONYMOUS_AUTH_PATH_PREFIXES.some(prefix => path.startsWith(prefix)) ||
    ANONYMOUS_AUTH_PATHS.includes(path)
  );
};

export const baseQueryWithReAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result?.error?.status === 401) {
    // send refresh token to get new access token
    const refreshResult = await baseQuery(
      {
        url: '/company/authenticate/refreshToken',
        method: 'POST',
        body: {
          refreshToken: (api.getState() as RootState).auth.refreshToken,
        },
      },
      api,
      extraOptions
    );

    // Optional-chained on purpose. A refresh that fails at the TRANSPORT level
    // — a non-2xx, a network drop, precisely the conditions under which a
    // session expires — resolves as `{ error }` with no `data`, and the old
    // unguarded cast `(refreshResult?.data as Response<Login>).data` threw a
    // TypeError right here: the logout-and-clear branch below never ran and the
    // expired tokens stayed in localStorage.
    const refreshData = (refreshResult?.data as Response<Login> | undefined)
      ?.data;

    // A half-built session is still a failed refresh: persisting `undefined` as
    // a token poisons every subsequent request. Both halves or neither.
    if (refreshData?.accessToken && refreshData?.refreshToken) {
      // store the new token
      api.dispatch(setCredentials(refreshData));

      localStorage.setItem('accessToken', refreshData.accessToken);
      localStorage.setItem('refreshToken', refreshData.refreshToken);

      // retry the original query with new access token
      result = await baseQuery(args, api, extraOptions);
    } else {
      api.dispatch(logout());

      ['accessToken', 'refreshToken'].forEach(key =>
        localStorage.removeItem(key)
      );
    }
  }

  if ([500, 400].includes(result?.error?.status)) {
    displayNotification({
      title: 'Something went wrong!',
      description: 'Please try this action later or contact BrickLynk support.',
      status: 'error',
    });

    return result;
  }

  if (result?.data?.errorCode === '1030') {
    // Anonymous auth endpoints: the page owns the copy, we say nothing.
    if (isAnonymousAuthRequest(args)) {
      return result;
    }

    displayNotification({
      title: result?.data.message,
      // `details` is NEVER rendered — for any endpoint. It is where core-api
      // puts exception-derived text (stack-adjacent internals, token-state
      // discriminators), which is not something a user can act on and not
      // something we hand out. Message-only.
      description: '',
      status: 'error',
    });

    return result;
  }

  return result;
};

export enum TagTypes {
  'Role',
  'User',
  'TeamMember',
  'Project',
  'Phase',
  'Document',
  'Form',
  'Workflow',
  'Task',
}

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReAuth,
  endpoints: () => ({}),
  tagTypes: Object.keys(TagTypes) as Array<keyof typeof TagTypes>,
});
